/**
 * i18n 入口
 *
 * 文案采用「中文原文做 key」模式（gettext 风格）：
 * - en-US.js 为「中文原文 → 英文」的扁平映射；zh-CN 的恒等映射（key 即 value）由下方按 en-US 的全部 key 自动生成
 * - 中文模式下 key 未命中时 vue-i18n 返回 key 本身（即中文原文）；其他语言未命中回退中文
 *
 * 扩展新语言的固定步骤（业务代码零改动）：
 * 1. src/i18n/lang/ 下新增语言扁平映射文件（参照 en-US.js 结构：key 用中文原文，value 用目标语言译文，缺失的 key 自动回退中文）
 * 2. SUPPORT_LANGS 注册表加一行：{ value: 'xx-XX', label: '语言名', element: '<element语言包名>', antd: '<antd语言包名>' }
 * 3. ELEMENT_PACKS / ANTD_PACKS 中补一行静态 require 映射
 */
import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { LANGUAGE } from '@/store/mutation-types'

import enUS from './lang/en-US'
import menuMap from './menuMap'
import buttonTextMap from './buttonTextMap'

Vue.use(VueI18n)

/**
 * 语言注册表（唯一切换点）
 * value   - 语言码（localStorage 存储 & vue-i18n messages 键）
 * label   - 切换下拉中显示的语言名（用该语言原文书写）
 * element - Element UI locale 包名（element-ui/lib/locale/lang/ 下）
 * antd    - Ant Design Vue locale 包名（ant-design-vue/lib/locale-provider/ 下）
 */
export const SUPPORT_LANGS = [
  { value: 'zh-CN', label: '简体中文', element: 'zh-CN', antd: 'zh_CN' },
  { value: 'en-US', label: 'English', element: 'en', antd: 'en_US' }
  // 未来扩展示例：{ value: 'fr-FR', label: 'Français', element: 'fr', antd: 'fr_FR' }
]

export const DEFAULT_LANG = 'zh-CN'

// zh-CN 恒等映射：中文原文做 key，value 即 key 本身（按 en-US.js 的全部 key 自动生成）
const zhMessages = {}
Object.keys(enUS).forEach(k => { zhMessages[k] = k })

// 各业务模块的英文映射（src/i18n/lang/en/modules/<模块名>.js），自动合并
const moduleFiles = require.context('./lang/en/modules', false, /\.js$/)
const moduleEn = {}
moduleFiles.keys().forEach(file => {
  Object.assign(moduleEn, moduleFiles(file).default || moduleFiles(file))
})
Object.keys(moduleEn).forEach(k => { zhMessages[k] = k })

// Element UI / Ant Design Vue 的 locale 包均为 exports.default 形式
const ELEMENT_PACKS = {
  'zh-CN': require('element-ui/lib/locale/lang/zh-CN').default,
  'en': require('element-ui/lib/locale/lang/en').default
}

// Ant Design Vue locale 包（供 <a-config-provider> 使用）
const ANTD_PACKS = {
  'zh_CN': require('ant-design-vue/lib/locale-provider/zh_CN').default,
  'en_US': require('ant-design-vue/lib/locale-provider/en_US').default
}

// 静态方法 Modal.confirm/info/error... 不经过 <a-config-provider>，
// 其按钮文案取自组件库运行时全局注册（changeConfirmLocale），需手动同步。
// 注意：必须用 es/ 路径 —— webpack 通过 package.json 的 module 字段加载 es/ 版本，
// lib/ 是另一个独立实例，patch 它不生效
const { changeConfirmLocale } = require('ant-design-vue/es/modal/locale')

/**
 * 同步 Antd 静态弹窗（$confirm 等）的默认按钮文案（确定/取消/知道了）
 */
export function syncAntdConfirmLocale(lang) {
  const conf = SUPPORT_LANGS.find(l => l.value === lang) || SUPPORT_LANGS[0]
  const pack = ANTD_PACKS[conf.antd]
  if (pack && pack.Modal) {
    changeConfirmLocale(pack.Modal)
  }
}

/**
 * 读取已保存的语言偏好
 * 说明：vue-ls 以 JSON 形式（{value, expire}）存储并带 pro__ 前缀，此处直接读 localStorage，
 * 不依赖 Vue.use(Storage) 的安装时机（本模块在 main.js 顶部即被引入）
 */
function getStoredLang() {
  try {
    const raw = localStorage.getItem('pro__' + LANGUAGE)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return (parsed && parsed.value) || raw
  } catch (e) {
    return null
  }
}

function getInitLang() {
  const stored = getStoredLang()
  return stored && SUPPORT_LANGS.some(l => l.value === stored) ? stored : DEFAULT_LANG
}

const i18n = new VueI18n({
  locale: getInitLang(),
  fallbackLocale: DEFAULT_LANG, // 未翻译的 key 自动回退中文
  silentTranslationWarn: true,
  messages: {
    'zh-CN': { ...zhMessages, ...ELEMENT_PACKS['zh-CN'] },
    'en-US': { ...moduleEn, ...enUS, ...ELEMENT_PACKS['en'] }
  }
})

// 初始化 Antd 静态弹窗默认按钮文案（需在 i18n 实例创建后调用）
syncAntdConfirmLocale(i18n.locale)

/**
 * 菜单标题翻译：按「当前语言 + 中文原名」查找 menuMap，缺失回退中文原文
 * 已挂载为 Vue 原型方法 $menuTitle
 */
export function translateMenuTitle(title) {
  if (!title) return title
  const map = menuMap[i18n.locale]
  return (map && map[title]) || title
}

/**
 * 按 SUPPOR_LANGS 注册表映射取 Element UI locale 包（保留给非 vue-i18n 集成场景使用）
 */
export function getElementLocale(lang) {
  const conf = SUPPORT_LANGS.find(l => l.value === lang) || SUPPORT_LANGS[0]
  return ELEMENT_PACKS[conf.element]
}

/**
 * 按注册表映射取 Ant Design Vue locale 包（用于 <a-config-provider :locale="...">）
 */
export function getAntdLocale(lang) {
  const conf = SUPPORT_LANGS.find(l => l.value === lang) || SUPPORT_LANGS[0]
  return ANTD_PACKS[conf.antd]
}

Vue.prototype.$menuTitle = translateMenuTitle

/**
 * 硬编码按钮文案翻译：用于 JModal 等公共组件出口处拦截业务代码写死的
 * 按钮文案（如 cancelText="关闭"），按 buttonTextMap 查找，缺失回退原文
 * 已挂载为 Vue 原型方法 $buttonText
 */
export function translateButtonText(text) {
  if (!text) return text
  const map = buttonTextMap[i18n.locale]
  return (map && map[text]) || text
}

Vue.prototype.$buttonText = translateButtonText

export default i18n
